import { Project, ProjectStatus, RDTIResponses, UpdateProjectRequestBody, UserRole } from '@avanzu/types';
import { resolveSchemaVersionForTaxYear } from '@avanzu/rdti-schema';
import { RDTIFormRenderer } from '../../../components/rdti/RDTIFormRenderer';
import {
  AssignmentOutlined,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services';
import { EmptyStateContent } from '../../../components/EmptyStateContent';
import { AiFieldEnhancer } from './AiFieldEnhancer';
import { getProjectQuotaInfo } from '../../../utils/quota';
import PlanDownloadButton from './PlanDownloadButton';

interface ProjectEditorProps {
  project: Project | null;
  projects: Project[];
  onProjectUpdate: (project: Project) => void;
  onProjectDelete: (project: Project) => void;
}

export const ProjectEditor: React.FC<ProjectEditorProps> = ({
  project,
  projects,
  onProjectUpdate,
  onProjectDelete,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [key, setKey] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [responses, setResponses] = useState<RDTIResponses>({});
  const [activeTab, setActiveTab] = useState(0);

  // Pin the rendered schema to the project's stored schemaVersion so opening
  // a historical FY24 project still shows the FY24 form, while new FY25
  // projects get the new question set.
  const schemaVersion =
    project?.schemaVersion ?? (project ? resolveSchemaVersionForTaxYear(project.taxYear) : 'FY25');

  const { organisationId, currentOrganisation } = useAuth();
  const isViewer = currentOrganisation?.role === UserRole.VIEWER;

  const getTaxYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let i = -1; i <= 3; i++) {
      const year = currentYear + i;
      options.push(`${year}-${(year + 1).toString().slice(-2)}`);
    }
    return options;
  };

  useEffect(() => {
    if (project) {
      setHasChanges(false);
      setError(null);
      setKey((prev) => prev + 1); // Force form reset
      setResponses(project.responses ?? {});
    }
  }, [project]);

  const handleResponsesChange = (next: RDTIResponses) => {
    setResponses(next);
    setHasChanges(true);
  };

  const validateForm = (formData: FormData) => {
    // Get name from either FormData or current project (if field is hidden in inactive tab)
    const name = (formData.get('name') as string) || project?.name;
    const taxYear = (formData.get('taxYear') as string) || project?.taxYear;
    const totalBudget = formData.has('totalBudget')
      ? Number(formData.get('totalBudget'))
      : project?.totalBudget || 0;

    if (!name?.trim()) {
      setError('Project name is required');
      setActiveTab(0); // Switch to the tab with the error
      return false;
    }

    if (!taxYear?.trim()) {
      setError('Tax year is required');
      setActiveTab(0); // Switch to the tab with the error
      return false;
    }

    if (totalBudget < 0) {
      setError('Total budget cannot be negative');
      setActiveTab(0); // Switch to the tab with the error
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!project || !formRef.current) return;

    const formData = new FormData(formRef.current);

    if (!validateForm(formData)) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Build request body with only the fields that are present
      const requestBody: UpdateProjectRequestBody = {};

      // Add fields from visible tab (FormData)
      if (formData.has('name')) {
        requestBody.name = formData.get('name') as string;
      }
      if (formData.has('taxYear')) {
        requestBody.taxYear = formData.get('taxYear') as string;
      }
      if (formData.has('totalBudget')) {
        requestBody.totalBudget = Number(formData.get('totalBudget'));
      }
      if (formData.has('rebatePercentage')) {
        requestBody.rebatePercentage = Number(formData.get('rebatePercentage')) / 100;
      }
      if (formData.has('status')) {
        requestBody.status = formData.get('status') as ProjectStatus;
      }
      // RDTI narrative answers — single source of truth is the `responses` JSON.
      (requestBody as unknown as { responses: RDTIResponses }).responses = responses;

      const response = await apiClient.put(
        `/organisations/${organisationId}/projects/${project.id}`,
        requestBody
      );
      const data = await response.json();

      if (data.success && data.project) {
        onProjectUpdate(data.project);
        setHasChanges(false);
      } else {
        setError(data.message || 'Failed to update project');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
    setDeleteConfirmation('');
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setDeleteConfirmation('');
  };

  const handleDeleteConfirm = async () => {
    if (!project || deleteConfirmation !== 'DELETE') return;

    try {
      const response = await apiClient.delete(
        `/organisations/${organisationId}/projects/${project.id}`
      );
      const data = await response.json();

      if (data.success) {
        setShowDeleteDialog(false);
        setDeleteConfirmation('');
        onProjectDelete(project);
      } else {
        setError(data.message || 'Failed to delete project');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    }
  };

  if (!project) {
    return (
      <EmptyStateContent
        icon={<AssignmentOutlined />}
        title="Select a project to edit"
        message="Choose a project from the list to view and edit its details"
      />
    );
  }

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">Project Editor</Typography>
              {project.aiGenerated && (
                <Chip
                  icon={<AutoFixHighIcon />}
                  label="AI Generated"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
            <PlanDownloadButton projectId={project.id} variant="outlined" size="small" />
          </Box>
        </Box>

        <Divider sx={{ mb: 0 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Project Details" />
          <Tab
            label="Project Content"
            icon={<AutoFixHighIcon sx={{ fontSize: 18 }} />}
            iconPosition="end"
          />
        </Tabs>

        <form ref={formRef} key={key} onChange={() => setHasChanges(true)}>
          {/* Tab Panel 1: Project Details */}
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                name="name"
                id="name"
                label="Project Name"
                defaultValue={project.name}
                required
                placeholder="e.g. FY2024 Software Development R&D"
                helperText="Enter a descriptive name for your R&D project"
                disabled={isViewer}
              />

              <TextField
                fullWidth
                name="description"
                id="description"
                label="Description (optional)"
                defaultValue={project.description || ''}
                placeholder="e.g. FY2024 Software Development R&D"
                helperText="Enter an optional summary or description for your R&D project"
                multiline
                minRows={2}
                disabled={isViewer}
              />

              <FormControl required sx={{ maxWidth: '50%' }}>
                <InputLabel>Tax Year</InputLabel>
                <Select
                  name="taxYear"
                  defaultValue={project.taxYear}
                  label="Tax Year"
                  onChange={() => setHasChanges(true)}
                  disabled={isViewer}
                >
                  {getTaxYearOptions().map((year) => {
                    // Count projects in this year, excluding the current project being edited
                    const projectsInYear = projects.filter(
                      (p) => p.taxYear === year && p.id !== project.id
                    ).length;
                    const quotaInfo = getProjectQuotaInfo(
                      currentOrganisation,
                      year,
                      projectsInYear
                    );

                    // Disable if at limit AND not the current year (allow keeping current year)
                    const isDisabled = quotaInfo.isAtLimit && year !== project.taxYear;

                    return (
                      <MenuItem key={year} value={year} disabled={isDisabled}>
                        <Box
                          sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                        >
                          <span>{year}</span>
                          {!quotaInfo.isUnlimited && year !== project.taxYear && (
                            <Chip
                              label={
                                quotaInfo.isAtLimit
                                  ? 'Limit reached'
                                  : `${quotaInfo.current}/${quotaInfo.limit}`
                              }
                              size="small"
                              color={quotaInfo.isAtLimit ? 'error' : 'default'}
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <TextField
                sx={{ maxWidth: '50%' }}
                name="rebatePercentage"
                id="rebatePercentage"
                label="Rebate Percentage"
                type="text"
                defaultValue={project.rebatePercentage * 100}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                helperText="Your R&D rebate percentage."
                disabled={isViewer}
              />

              <TextField
                sx={{ maxWidth: '50%' }}
                name="totalBudget"
                id="totalBudget"
                label="Total Budget"
                type="text"
                defaultValue={project.totalBudget}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                helperText="Estimated total budget for all R&D activities in this project"
                disabled={isViewer}
              />

              <FormControl sx={{ maxWidth: '50%' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  defaultValue={project.status}
                  label="Status"
                  onChange={() => setHasChanges(true)}
                  disabled={isViewer}
                >
                  <MenuItem value={ProjectStatus.DRAFT}>Draft</MenuItem>
                  <MenuItem value={ProjectStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={ProjectStatus.SUBMITTED}>Submitted</MenuItem>
                  <MenuItem value={ProjectStatus.COMPLETED}>Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Tab Panel 2: RDTI form (schema-driven) */}
          {activeTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body2" color="text.secondary">
                AusIndustry registration questions for {schemaVersion}. Enhance any field with
                Avanzu AI to improve compliance.
              </Typography>
              <RDTIFormRenderer
                schemaVersion={schemaVersion}
                section="project"
                responses={responses}
                onChange={handleResponsesChange}
                disabled={isViewer}
                renderFieldAccessory={(field) =>
                  organisationId ? (
                    <AiFieldEnhancer
                      organisationId={organisationId}
                      projectId={project.id}
                      fieldName={field.key}
                      currentValue={(responses[field.key] as string) || ''}
                      projectContext={{
                        name: project.name,
                        industrySector: project.industrySector || undefined,
                      }}
                      onApply={(enhanced) =>
                        handleResponsesChange({ ...responses, [field.key]: enhanced })
                      }
                      disabled={isViewer}
                      quotaAiEnabled={currentOrganisation?.quotaAiEnabled}
                    />
                  ) : null
                }
              />
            </Box>
          )}
        </form>

        <Box sx={{ mt: 2, pt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !hasChanges || isViewer}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={saving || isViewer}
          >
            Delete
          </Button>
        </Box>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              This action cannot be undone
            </Typography>
            <Typography variant="body2">Deleting this project will permanently remove:</Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li>All project data and settings</li>
              <li>All research activities</li>
              <li>All transaction allocations</li>
              <li>All employee and asset allocations</li>
            </Box>
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm deletion, please type <strong>DELETE</strong> in the box below:
          </Typography>

          <TextField
            fullWidth
            name="deleteConfirmation"
            id="deleteConfirmation"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            placeholder="Type DELETE to confirm"
            autoFocus
            error={deleteConfirmation.length > 0 && deleteConfirmation !== 'DELETE'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteConfirmation !== 'DELETE'}
          >
            Delete Project
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ProjectEditor;
